import { useEffect, useState } from 'react';
import { api } from '../api/shop';
import { apiUrl } from '../api/client';
import { QrCode } from './QrCode';
import { brand, formatMoney } from '../brand/brand';
import type { MemberCard } from '../api/types';

/**
 * The customer's membership card, and the way it gets into a phone's wallet.
 *
 * WHAT IS NOT ON THIS CARD: a balance. One card serves every partner and a balance
 * is per partner, so there is no single number that is true. The card identifies;
 * the counter looks up what the customer holds with THAT business and nothing else.
 *
 * The QR code is bearer material — scanning it authorises a spend — which is why
 * this component has a Reset button at all, and why that button is the second most
 * prominent thing on the panel rather than buried in settings. The customer whose
 * phone was photographed at a bar needs it at 11pm, not on Monday.
 */
export function MemberCardPanel() {
  const [card, setCard] = useState<MemberCard | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    api.membership()
      .then((r) => { if (!cancelled) setCard(r); })
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, []);

  async function openPass(which: 'apple' | 'google') {
    setBusy(true);
    setNotice(null);

    try {
      const links = await api.walletPassLinks();
      const url = which === 'apple' ? links.appleUrl : links.googleUrl;

      if (url === null) {
        setNotice('That wallet is not available right now.');
        return;
      }

      // A full navigation, so the browser - not this script - receives the pass.
      window.location.assign(apiUrl(url));
    } catch {
      setNotice('Could not get your pass just now. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    setBusy(true);
    setNotice(null);

    try {
      setCard(await api.resetMembership('Reset by the customer.'));
      setConfirming(false);
      setNotice('New card issued. The old QR code no longer works.');
    } catch {
      setNotice('Could not issue a new card just now. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (failed) {
    return (
      <p className="notice error" role="alert">
        We could not load your card just now. Please try again.
      </p>
    );
  }

  if (card === null) {
    return (
      <div role="status">
        <span className="visually-hidden">Loading your membership card…</span>
        <div className="skeleton" style={{ height: 220, borderRadius: 22 }} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 22 }}>
      <div className="memcard">
        <div className="memcard-top">
          <span className="memcard-brand">
            <span className="memcard-mark" aria-hidden="true" />
            {brand.name}
          </span>
          <span className="memcard-kind">Membership</span>
        </div>

        <p className="memcard-name">{card.displayName}</p>
        <p className="memcard-number">{card.memberNumber}</p>

        <div className="memcard-plate">
          {/*
            A REAL QR now, verified against a reference encoder and decoded in
            scripts/qr-verify.mjs. What was here before was a drawing — honest
            about it in its own comment, and unreadable by any scanner, which
            left the cashier typing the member number by hand.

            The member number stays underneath. A phone with a cracked screen or
            a dead camera at the counter still needs the fallback.
          */}
          <QrCode value={card.barcodeValue} size={190} />
          <p className="memcard-alt">{card.memberNumber}</p>
        </div>
      </div>

      <section className="stack" style={{ gap: 10 }}>
        <h2>Keep it in your phone</h2>
        <p>
          Add it to your wallet and it is on your lock screen at the counter — no app,
          no signing in, no signal needed.
        </p>

        {/*
          THE BROWSER NAVIGATES, JavaScript does not fetch. iOS hands a .pkpass to
          Wallet only when Safari itself receives it, and the Google route is a
          redirect to a signed save link.

          But a plain link cannot carry the sign-in (it lives in memory and goes in a
          header), so the old <a href> buttons were answered with 401. Now a tap asks
          the API for a one-minute link for THIS customer, then navigates to it.

          Both buttons show on every device. A desktop or an Android phone can still
          open the Apple link - it just downloads a file - and hiding buttons by
          sniffing the user agent gets it wrong for exactly the people who most need
          it to work.
        */}
        {(card.appleWalletAvailable || card.googleWalletAvailable) ? (
          <div className="stack-h wrap" style={{ gap: 10 }}>
            {card.appleWalletAvailable && (
              <button type="button" className="btn" disabled={busy} onClick={() => void openPass('apple')}>
                Add to Apple Wallet
              </button>
            )}
            {card.googleWalletAvailable && (
              <button type="button" className="btn secondary" disabled={busy} onClick={() => void openPass('google')}>
                Save to Google Wallet
              </button>
            )}
          </div>
        ) : (
          /* Not disabled buttons. Somebody would tap one and wonder what is wrong. */
          <p className="hint">
            Wallet passes are not switched on yet. Your card works here in the meantime.
          </p>
        )}
      </section>

      <section className="stack" style={{ gap: 10 }}>
        <h2>What a partner sees</h2>
        <p>
          When you show this, they see the credit you hold <em>with them</em> — nothing
          about anywhere else you shop, and nothing about what else you have bought.
        </p>
        <p>
          A scan can cover up to {formatMoney(card.perTransactionCapCents)} at once, and{' '}
          {formatMoney(card.dailyCapCents)} a day. Above that, open the voucher here
          and show its own code.
        </p>
      </section>

      <section className="stack" style={{ gap: 10 }}>
        <h2>Lost your phone?</h2>
        <p>
          Get a new card. The old QR code stops working straight away, and your
          vouchers are untouched.
        </p>

        {notice !== null && <p className="notice" role="status">{notice}</p>}

        {confirming ? (
          <div className="stack-h wrap" style={{ gap: 10 }}>
            <button type="button" className="btn" onClick={() => void reset()} disabled={busy}>
              {busy ? 'Issuing…' : 'Yes, replace my card'}
            </button>
            <button type="button" className="btn secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Keep this one
            </button>
          </div>
        ) : (
          /* Confirmed first: this invalidates a card already added to a wallet, and
             a stray tap should not silently break the thing in their pocket. */
          <button
            type="button"
            className="btn secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => { setConfirming(true); setNotice(null); }}
          >
            Replace my card
          </button>
        )}
      </section>
    </div>
  );
}
